import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../api.service';
import { CountryModel } from '../../models/customer-maintenance/country.model';
import { CompanyModel } from '../../models/customer-maintenance/company.model';
import { map } from 'rxjs/operators';
import { CustomerTypeModel } from '../../models/master-list/customer-type.model';
import { DashboardModel } from '../../models/customer-maintenance/dashboard.model';
import { CustomerModel } from '../../models/customer-maintenance/customer.model';
import { ContactModel } from '../../models/customer-maintenance/contact.model';
import { NotesModel } from '../../models/customer-maintenance/notes.model';
import { ContractModel } from '../../models/customer-maintenance/contract.model';
import { CarriersModel } from '../../models/customer-maintenance/carriers.model';
import { CarrierRequestModel } from '../../models/master-list/carrier.request.model';
import { CarrierModel } from '../../models/master-list/carrier.model';
import { CustomerCarrierModel } from '../../models/master-list/customer-carrier.model';
import { ReferenceNumberTypeModel } from '../../models/master-list/reference-number-type.model';
import { OnboardingPhaseModel } from '../../models/customer-maintenance/onboarding/onboarding-phase.model';
import { OnboardingDocumentModel } from '../../models/customer-maintenance/onboarding/onboarding-document.model';
import { AddressModel } from '../../models/address.model';
import { VolumeModel } from '../../models/customer-maintenance/volume.model';
import { RevenueModel } from '../../models/customer-maintenance/revenue.model';
import { CarrierSearchModel } from '../../models/customer-maintenance/carrier-search.model';
import { OnboardingAdditionalUserModel } from '../../models/customer-maintenance/onboarding/onboarding-additional-user.model';
import { CustAuditTrailDialogComponent } from '../../../customer/customer-maint-detail/cust-audit-trail-dialog/cust-audit-trail-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';
import { CustomerDocumentTypeModel } from '../../models/lynnco-admin/customer-document-type.model';
import { ParentCustomerModel } from '../../models/master-list/parent-customer.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerAdminService {
  constructor(
    private readonly apiService: ApiService,
    private readonly dialog: MatDialog,
  ) {}

  getCustomers(search: any): Observable<any> {
    return this.apiService.get<any>(environment.hairball.getCustomers + search);
  }

  getCountries(): Observable<CountryModel[]> {
    return this.apiService.get<CountryModel[]>(environment.hairball.country);
  }

  getCompanies(data: {
    countries: string;
    province: string;
    name: string;
  }): Observable<CompanyModel[]> {
    return this.apiService.post<CompanyModel[]>(
      environment.hairball.creditSave,
      data,
    );
  }

  customerMaintenance(id: number): Observable<CustomerModel> {
    return this.apiService
      .get<CustomerModel>(environment.hairball.customerMaintenance + id)
      .pipe(map((response: any) => response?.Data));
  }

  getCustomerContracts(id: number): Observable<ContractModel[]> {
    return this.apiService
      .get(environment.hairball.getContracts + id)
      .pipe(map((response: any) => response?.Data));
  }

  getCustomerDocumentTypes(): Observable<CustomerDocumentTypeModel[]> {
    return this.apiService.get<CustomerDocumentTypeModel[]>(
      environment.lynncoAdmin.customerDocumentType.getAll,
    );
  }

  private createCustomerContract(data: any): Observable<any> {
    return this.apiService.post<any>(environment.hairball.createContract, data);
  }

  private updateCustomerContract(data: any): Observable<any> {
    return this.apiService.put<any>(environment.hairball.updateContract, data);
  }

  manageCustomerContract(action: string, data: any): Observable<any> {
    if (action === 'edit') {
      return this.updateCustomerContract(data);
    } else {
      return this.createCustomerContract(data);
    }
  }

  getCustomerType(typeId = ''): Observable<CustomerTypeModel[]> {
    const url = `${environment.masterList.customerType}/${typeId}`;
    return this.apiService.get<CustomerTypeModel[]>(url);
  }

  saveCustomer(data: any): Observable<{ CustomerID: string }> {
    return this.apiService.post<{ CustomerID: string }>(
      environment.hairball.saveCustomer,
      data,
    );
  }

  updateAccountInfo(data: any): Observable<any> {
    return this.apiService.put<any>(
      environment.hairball.customerMaintenance,
      data,
    );
  }

  getCustomerAddresses(id: any): Observable<AddressModel[]> {
    return this.apiService.get<AddressModel[]>(
      environment.hairball.customerAddress + id,
    );
  }

  updateAddressInfo(data: any): Observable<any> {
    return this.apiService.put<any>(environment.hairball.customerAddress, data);
  }

  createCustomerAddress(data: any): Observable<any> {
    return this.apiService.post<any>(
      environment.hairball.customerAddress,
      data,
    );
  }

  getCustomerContacts(id: any): Observable<ContactModel[]> {
    return this.apiService.get<ContactModel[]>(
      environment.hairball.customerContact + id,
    );
  }

  manageContact(action: string, data: any): Observable<any> {
    if (action === 'edit') {
      return this.updateContactInfo(data);
    } else {
      return this.createContact(data);
    }
  }

  updateContactInfo(data: any): Observable<any> {
    return this.apiService.post<any>(
      environment.hairball.contactMaintenanceProfile,
      data,
    );
  }

  createContact(data: any): Observable<any> {
    return this.apiService.post<any>(
      environment.hairball.customerContact,
      data,
    );
  }

  deleteContact(data: any): Observable<any> {
    return this.apiService.deleteBody<any>(
      environment.hairball.customerContact,
      data,
    );
  }

  getCustomerTypes(): Observable<any> {
    return this.apiService.get<any>(environment.masterList.customerType);
  }

  getCustomerNotes(id: number): Observable<NotesModel[]> {
    return this.apiService.get<NotesModel[]>(
      environment.hairball.customerNotes + id,
    );
  }

  getContactTypes(): Observable<any> {
    return this.apiService.get<any>(environment.hairball.contactType);
  }

  createNewCustomerNote(data: any): Observable<any> {
    return this.apiService.post<any>(environment.hairball.customerNotes, data);
  }

  deleteCustomerNote(data: any): Observable<any> {
    return this.apiService.put<any>(environment.hairball.customerNotes, data);
  }

  updateCustomerNote(data: any): Observable<any> {
    return this.apiService.put<any>(environment.hairball.customerNotes, data);
  }

  getDashboardInfo(): Observable<DashboardModel[]> {
    return this.apiService.get<DashboardModel[]>(
      environment.hairball.dashboard,
    );
  }

  manageLocation(data: any): Observable<any> {
    return this.apiService.post<any>(
      environment.hairball.customerLocations,
      data,
    );
  }

  getUsers(id: number): Observable<any> {
    return this.apiService.get<any>(environment.hairball.customerUsers + id);
  }

  getCarriers(id: number): Observable<CarriersModel[]> {
    return this.apiService
      .get<CarriersModel[]>(environment.hairballCarrier.customer + id)
      .pipe(
        map((carriers: CarriersModel[]) => this.formatRelationship(carriers)),
      );
  }

  manageCarrier(
    action: string,
    data: CarrierRequestModel,
  ): Observable<CarrierRequestModel> {
    if (action === 'add') {
      return this.createCarrier(data);
    } else {
      return this.updateCarrier(data);
    }
  }

  updateCarrier(data: CarrierRequestModel): Observable<CarrierRequestModel> {
    return this.apiService.put<CarrierRequestModel>(
      environment.hairballCarrier.customer,
      data,
    );
  }

  createCarrier(data: CarrierRequestModel): Observable<CarrierRequestModel> {
    return this.apiService.post<CarrierRequestModel>(
      environment.hairballCarrier.customer,
      data,
    );
  }

  getAllCarriers(): Observable<CarrierModel[]> {
    const url = environment.masterList.carrier;
    return this.apiService.get<CarrierModel[]>(url);
  }

  getRelationshipTypes(): Observable<any> {
    return this.apiService.get<any>(
      environment.hairball.carrierCustomerRelationshipTypes,
    );
  }

  getCustomerCarriers(customerId: number): Observable<CustomerCarrierModel[]> {
    const url = environment.masterList.customerCarrier + customerId;
    return this.apiService.get<CustomerCarrierModel[]>(url);
  }

  updateCreditLimit(data: any): Observable<any> {
    return this.apiService.post<any>(
      environment.hairball.editCreditLimit,
      data,
    );
  }

  getReferenceNumbers(): Observable<ReferenceNumberTypeModel[]> {
    return this.apiService.get<ReferenceNumberTypeModel[]>(
      environment.masterList.referenceNumberType,
    );
  }

  getOnboardingPhases(customerId: number): Observable<OnboardingPhaseModel> {
    return this.apiService
      .get<
        OnboardingPhaseModel[]
      >(environment.hairball.onboardingPhases + customerId)
      .pipe(
        map((customers: any) => (customers?.length > 0 ? customers[0] : null)),
      );
  }

  markPhaseAsCompleted(data: any): Observable<any> {
    return this.apiService.post<any>(
      environment.hairball.updateOnboardingPhase,
      data,
    );
  }

  editPaymentTerms(data: any): Observable<any> {
    return this.apiService.post<any>(
      environment.hairball.invoicing.paymentTerms,
      data,
    );
  }

  autoUpdateCreditScore(companies: string): Observable<number> {
    return this.apiService
      .post<any>(environment.hairball.invoicing.autoCreditScore, { companies })
      .pipe(
        map(
          (response: any) =>
            response.report.companySummary.creditRating.providerValue.value,
        ),
      );
  }

  manualCreditScoreUpdate(data: any): Observable<any> {
    return this.apiService.post<any>(
      environment.hairball.invoicing.creditScore,
      data,
    ).pipe(
      map((response: any) => ({
        CustomerCreditReportID: response.customerCreditReportID,
        CustomerID: response.customerID,
        CreditScoreCurrent: response.creditScoreCurrent,
        ReportRunDate: response.reportRunDate
      }))
    );
  }

  updateCustomerContact(data: any): Observable<any> {
    return this.apiService.put<any>(
      environment.hairball.updateCustomerContact,
      data,
    );
  }

  updateInvoicing(data: any, action: string): Observable<any> {
    if (action === 'add') {
      return this.apiService.post<any>(
        environment.hairball.invoicing.customerInvoiceConfig.create,
        data,
      );
    } else {
      return this.apiService.put<any>(
        environment.hairball.invoicing.customerInvoiceConfig.update,
        data,
      );
    }
  }

  sendOnboardingFormEmail(data: any): Observable<any> {
    return this.apiService.post<any>(
      environment.hairball.onboardingEmail,
      data,
    );
  }

  getOnboardingDocuments(
    customerId: number,
  ): Observable<OnboardingDocumentModel[]> {
    return this.apiService.get<OnboardingDocumentModel[]>(
      environment.hairball.onboardingDocs + customerId,
    ).pipe(map((response: any) => response?.Data || []));
  }

  getReportVolume(customerId: number): Observable<VolumeModel[]> {
    return this.apiService.get<VolumeModel[]>(
      environment.hairball.reportVolume + customerId,
    );
  }

  getReportRevenue(customerId: number): Observable<RevenueModel[]> {
    return this.apiService.get<RevenueModel[]>(
      environment.hairball.reportRevenue + customerId,
    );
  }

  getCarrierSearch(carrier: string): Observable<CarrierSearchModel[]> {
    return this.apiService.get<CarrierSearchModel[]>(
      environment.hairball.carrierSearch + carrier,
    );
  }

  private formatRelationship(list: CarriersModel[]): CarriersModel[] {
    list?.forEach((carrier) => {
      carrier.RelationshipStatus =
        carrier.CarrierActive && carrier.CustomerCarrierRelID === 2;
    });
    return list;
  }

  getOnboardingAdditionalUsers(
    customerID: number,
  ): Observable<OnboardingAdditionalUserModel[]> {
    return this.apiService.get<OnboardingAdditionalUserModel[]>(
      environment.hairball.onboardingAdditionalUsers + customerID,
    );
  }

  getCustomerAuditTrail(
    customerTrail: string,
    customerID: number,
  ): Observable<any[]> {
    return this.apiService.get<any[]>(
      environment.hairball.customerAuditTrail +
        `${customerTrail}&${customerID}`,
    );
  }

  uploadExpenseCodes(file: any, customerId: number): Observable<any> {
    return this.apiService.post<any>(
      environment.hairball.uploadExpenseCodeFile + customerId,
      file,
    );
  }

  openAuditTrailDialog(
    customerTrail: string,
    auditTitle: string,
    customerID: number,
  ): void {
    this.dialog.open(CustAuditTrailDialogComponent, {
      width: '850pt',
      maxWidth: '90vw',
      autoFocus: false,
      disableClose: true,
      data: {
        auditPath: customerTrail,
        auditTitle: auditTitle,
        customerID: customerID,
      },
    });
  }

  updateCustomerStatus(data: any): Observable<any> {
    return this.apiService.put<any>(
      environment.hairball.updateCustomerStatus,
      data,
    );
  }

  getParentCustomers(): Observable<ParentCustomerModel[]> {
    return this.apiService
      .get<any>(environment.masterList.parentCustomers)
      .pipe(map((response: any) => response?.data || []));
  }

}
